/**
 * Account setting: what the agent may do without asking, in every project you
 * own (a project can override each one in its own settings).
 *
 * - Full access — the sandbox can reach any public host and destructive
 *   commands run without a confirm. Private and internal addresses stay
 *   unreachable either way.
 * - Auto-approve commands / plans — skip those two prompts.
 * - Approved hosts — every host you allowed once; none of your projects asks
 *   about it again. Remove one to be asked again.
 *
 * Backed by `GET/PATCH /account/sandbox-permissions` and
 * `DELETE /account/sandbox-permissions/hosts`. Registered accounts only: the
 * section renders nothing for a guest.
 */
import { type JSX, useEffect, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Switch } from '@molecule/app-ui-react'

/** The account permissions the API returns. */
interface AccountPermissions {
  sandboxAccess: 'ask' | 'full'
  autoApproveCommands: boolean
  autoApprovePlans: boolean
  egressAllowlist: string[]
}

type Flag = 'sandboxAccess' | 'autoApproveCommands' | 'autoApprovePlans'

/**
 * The account's agent-permission switches and approved hosts.
 *
 * @returns The section, or null for a guest.
 */
export function AccountSandboxSection(): JSX.Element | null {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  const [permissions, setPermissions] = useState<AccountPermissions | null>(null)
  const [hidden, setHidden] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    http
      .get<{ permissions: AccountPermissions }>('/account/sandbox-permissions')
      .then((res) => {
        if (!cancelled) setPermissions(res.data.permissions)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status === 401 || status === 403) setHidden(true)
        else setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [http])

  if (hidden) return null

  const save = (patch: Partial<Record<Flag, unknown>>): void => {
    const previous = permissions
    setBusy(true)
    setFailed(false)
    setPermissions((p) => (p ? ({ ...p, ...patch } as AccountPermissions) : p))
    http
      .patch<{ permissions: AccountPermissions }>('/account/sandbox-permissions', patch)
      .then((res) => setPermissions(res.data.permissions))
      .catch(() => {
        setPermissions(previous)
        setFailed(true)
      })
      .finally(() => setBusy(false))
  }

  const removeHost = (host: string): void => {
    setBusy(true)
    setFailed(false)
    http
      .delete<{ permissions: AccountPermissions }>('/account/sandbox-permissions/hosts', {
        data: { host },
      })
      .then((res) => setPermissions(res.data.permissions))
      .catch(() => setFailed(true))
      .finally(() => setBusy(false))
  }

  const full = permissions?.sandboxAccess === 'full'
  const rows: Array<{
    key: Flag
    label: string
    desc: string
    checked: boolean
    locked?: boolean
  }> = [
    {
      key: 'sandboxAccess',
      label: t('settings.sandbox.fullAccess.label', undefined, { defaultValue: 'Full access' }),
      desc: t('settings.sandbox.fullAccess.desc', undefined, {
        defaultValue:
          'The agent never stops to ask: any public website or API is reachable and commands run without a confirm. Private and internal addresses stay blocked.',
      }),
      checked: full,
    },
    {
      key: 'autoApproveCommands',
      label: t('settings.sandbox.autoApproveCommands.label', undefined, {
        defaultValue: 'Auto-approve commands',
      }),
      desc: t('settings.sandbox.autoApproveCommands.desc', undefined, {
        defaultValue: 'Skip the confirm before a destructive command, such as a recursive delete.',
      }),
      checked: full || !!permissions?.autoApproveCommands,
      locked: full,
    },
    {
      key: 'autoApprovePlans',
      label: t('settings.sandbox.autoApprovePlans.label', undefined, {
        defaultValue: 'Auto-approve plans',
      }),
      desc: t('settings.sandbox.autoApprovePlans.desc', undefined, {
        defaultValue: 'Start building as soon as the plan is written, without the review step.',
      }),
      checked: !!permissions?.autoApprovePlans,
    },
  ]

  return (
    <div data-mol-id="settings-sandbox-permissions" className={cm.sp('mt', 4)}>
      <p className={cm.cn(cm.textSize('sm'), cm.fontWeight('semibold'))}>
        {t('settings.sandbox.heading', undefined, { defaultValue: 'Agent permissions' })}
      </p>
      <p className={cm.cn(cm.textSize('xs'), cm.textMuted, cm.sp('mb', 2))}>
        {t('settings.sandbox.intro', undefined, {
          defaultValue:
            'Defaults for every project you own. Each project can override them in its settings.',
        })}
      </p>
      {rows.map((row) => (
        <div key={row.key} className={cm.sp('mb', 2)}>
          <Flex align="center" justify="between" gap="sm">
            <span className={cm.textSize('sm')}>{row.label}</span>
            <Switch
              checked={row.checked}
              disabled={busy || permissions === null || row.locked}
              size="sm"
              aria-label={row.label}
              data-mol-id={`settings-sandbox-${row.key}`}
              onChange={(e) => {
                const on = (e.target as HTMLInputElement).checked
                save({ [row.key]: row.key === 'sandboxAccess' ? (on ? 'full' : 'ask') : on })
              }}
            />
          </Flex>
          <p className={cm.cn(cm.textSize('xs'), cm.textMuted, cm.sp('mt', 1))}>{row.desc}</p>
        </div>
      ))}
      <p className={cm.cn(cm.textSize('sm'), cm.sp('mt', 3))}>
        {t('settings.sandbox.hosts.label', undefined, { defaultValue: 'Approved hosts' })}
      </p>
      {permissions && permissions.egressAllowlist.length === 0 ? (
        <p className={cm.cn(cm.textSize('xs'), cm.textMuted)}>
          {t('settings.sandbox.hosts.empty', undefined, {
            defaultValue: 'None yet. A host you allow once is listed here and never asked again.',
          })}
        </p>
      ) : (
        <ul data-mol-id="settings-sandbox-hosts">
          {(permissions?.egressAllowlist ?? []).map((host) => (
            <li key={host} className={cm.sp('mb', 1)}>
              <Flex align="center" justify="between" gap="sm">
                <code className={cm.textSize('xs')}>{host}</code>
                <Button
                  size="sm"
                  variant="ghost"
                  className={cm.touchTargetCompact}
                  disabled={busy}
                  data-mol-id="settings-sandbox-host-remove"
                  aria-label={t(
                    'settings.sandbox.hosts.removeLabel',
                    { host },
                    { defaultValue: 'Remove {{host}}' },
                  )}
                  onClick={() => removeHost(host)}
                >
                  {t('settings.sandbox.hosts.remove', undefined, { defaultValue: 'Remove' })}
                </Button>
              </Flex>
            </li>
          ))}
        </ul>
      )}
      {failed && (
        <p className={cm.cn(cm.textSize('xs'), cm.textError, cm.sp('mt', 1))}>
          {t('settings.sandbox.failed', undefined, {
            defaultValue: 'Could not save this setting. Try again.',
          })}
        </p>
      )}
    </div>
  )
}
